import type React from 'react'
import { useNavigate } from 'react-router-dom'
import type { ICellRendererParams } from 'ag-grid-community'
import type { RecMatchRow } from '../../../../../types/api/v1'
import { handleNavigation } from '../../utils/navigationHelpers'
import { generateNavigationUrlForMatch } from '../../Launchpad/components/utils/navigationUtils'

interface InvoiceNumberClickableCellRendererProps extends ICellRendererParams {
  data: {
    invoice_number: string
    supplier_name: string
    match_id?: string
  }
  context?: {
    matches?: RecMatchRow[]
    assignedToMe?: boolean
  }
}

export function InvoiceNumberClickableCellRenderer({ data, context }: InvoiceNumberClickableCellRendererProps) {
  const navigate = useNavigate()
  const matches = context?.matches || []
  const assignedToMe = context?.assignedToMe || false

  function handleClick(event: React.MouseEvent) {
    if (data?.invoice_number) {
      // Use match ID for direct navigation (like Launchpad approach)
      if (data.match_id) {
        const matchData = matches.find((match) => match.id === data.match_id)
        if (matchData) {
          const navigationResult = generateNavigationUrlForMatch(matchData, data.invoice_number)
          handleNavigation(navigationResult.url, navigate, event)
          return
        }
      }

      // Fallback: Try to find a match that contains this invoice number
      const matchData = matches.find(
        (match) =>
          match.invoice_number === data.invoice_number ||
          (match.supplier_name === data.supplier_name && match.invoice_number === data.invoice_number)
      )

      if (matchData) {
        const navigationResult = generateNavigationUrlForMatch(matchData, data.invoice_number)
        handleNavigation(navigationResult.url, navigate, event)
      } else {
        // Final fallback to general Match View with invoice filter
        const assignedParam = assignedToMe ? '&assigned_to_me=true' : ''
        const url = `/supplier_rec/match_view?invoice_number=${encodeURIComponent(data.invoice_number)}${assignedParam}`
        handleNavigation(url, navigate, event)
      }
    }
  }

  return (
    <span
      style={{
        color: 'var(--theme-color-2)',
        cursor: 'pointer',
        textDecoration: 'underline',
        fontSize: '12px',
      }}
      onClick={handleClick}
    >
      {data?.invoice_number || 'N/A'}
    </span>
  )
}
